import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { publishToast, subscribeToToasts, type ToastInput, type ToastMessage } from './toastBus';

/**
 * Toast provider — renders one MUI `<Snackbar>` at a time (bottom-center),
 * fed by the module-level `toastBus`. Mount once near the app root.
 *
 * The provider subscribes to the bus so both React callers (via
 * `useToast()`) and non-React callers (the axios interceptor) funnel
 * through the same queue. Messages are shown FIFO — a newer toast
 * replaces the current one (admin tooling favours the latest signal).
 */
const DEFAULT_DURATION_MS = 6000;

interface ToastContextValue {
  showToast: (input: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [current, setCurrent] = useState<ToastMessage | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToToasts((toast) => {
      setCurrent(toast);
      setOpen(true);
    });
    return unsubscribe;
  }, []);

  const showToast = useCallback((input: ToastInput) => {
    publishToast(input);
  }, []);

  const handleClose = useCallback((_event: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={current?.durationMs ?? DEFAULT_DURATION_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {current ? (
          <Alert
            severity={current.severity}
            variant="filled"
            onClose={() => setOpen(false)}
            sx={{ width: '100%' }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
};

/**
 * Hook for React callers to push a toast. Resolve `t()` before calling
 * so the bus carries display-ready text (CLAUDE.md absolute rule #11 —
 * no raw keys cross the bus).
 */
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() called outside <ToastProvider>. Wrap the tree at App.tsx.');
  }
  return ctx;
};
